import React, { Children, cloneElement, forwardRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Validation from './Validation';

export const ChecksGroup = forwardRef(
	(
		{
			id,
			className,
			children,
			isInline,
			isValid,
			isTouched,
			invalidFeedback,
			validFeedback,
			isTooltipFeedback,
			...props
		},
		ref,
	) => {
		return (
			<>
				<div
					className={classNames(
						{
							'is-invalid': !isValid && isTouched && invalidFeedback,
							'is-valid': !isValid && isTouched && !invalidFeedback,
						},
						className,
					)}
					id={id}
					ref={ref}
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...props}
				>
					{Children.map(children, (child) =>
						cloneElement(child, {
							isInline: child.props.isInline || isInline,
							isValid,
							isTouched,
							invalidFeedback,
							validFeedback,
							isTooltipFeedback,
							isValidMessage: false,
						}),
					)}
				</div>
				<Validation
					invalidFeedback={invalidFeedback}
					isTooltip={isTooltipFeedback}
					isTouched={isTouched}
					validFeedback={validFeedback}
				/>
			</>
		);
	},
);
ChecksGroup.propTypes = {
	id: PropTypes.string,
	className: PropTypes.string,
	children: PropTypes.node.isRequired,
	isInline: PropTypes.bool,
	isTouched: PropTypes.bool,
	isValid: PropTypes.bool,
	invalidFeedback: PropTypes.string,
	validFeedback: PropTypes.string,
	isTooltipFeedback: PropTypes.bool,
};
ChecksGroup.defaultProps = {
	id: null,
	className: null,
	isInline: false,
	isTouched: false,
	isValid: false,
	invalidFeedback: null,
	validFeedback: null,
	isTooltipFeedback: false,
};

const Checks = forwardRef(
	(
		{
			id,
			className,
			name,
			type,
			label,
			value,
			checked,
			disabled,
			isInline,
			isFormCheckInput,
			isValid,
			isTouched,
			invalidFeedback,
			validFeedback,
			isValidMessage,
			isTooltipFeedback,
			onBlur,
			onChange,
			onFocus,
			onInput,
			onInvalid,
			onSelect,
			ariaLabel,
			...props
		},
		ref,
	) => {
		const _inner = (
			<input
				aria-label={ariaLabel}
				checked={type === 'radio' ? checked === value : checked}
				className={classNames(
					'form-check-input',
					{
						'mt-0': isFormCheckInput,
						'is-invalid': !isValid && isTouched && invalidFeedback,
						'is-valid': !isValid && isTouched && !invalidFeedback,
					},
					className,
				)}
				disabled={disabled}
				id={id}
				name={name === null ? id : name}
				ref={ref}
				type={type === 'radio' ? 'radio' : 'checkbox'}
				value={value}
				onBlur={onBlur}
				onChange={onChange}
				onFocus={onFocus}
				onInput={onInput}
				onInvalid={onInvalid}
				onSelect={onSelect}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...props}
			/>
		);

		if (isFormCheckInput) {
			return _inner;
		}
		return (
			<div
				className={classNames('form-check', {
					'form-switch': type === 'switch',
					'form-check-inline': isInline,
				})}
			>
				{_inner}
				{label && (
					<label className='form-check-label' htmlFor={id}>
						{label}
					</label>
				)}
				{isValidMessage && (
					<Validation
						invalidFeedback={invalidFeedback}
						isTooltip={isTooltipFeedback}
						isTouched={isTouched}
						validFeedback={validFeedback}
					/>
				)}
			</div>
		);
	},
);
Checks.propTypes = {
	id: PropTypes.string,
	className: PropTypes.string,
	name: PropTypes.string,
	type: PropTypes.oneOf(['checkbox', 'radio', 'switch']),
	label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
	// eslint-disable-next-line react/require-default-props
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	checked: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
	disabled: PropTypes.bool,
	isInline: PropTypes.bool,
	isFormCheckInput: PropTypes.bool,
	isTouched: PropTypes.bool,
	isValid: PropTypes.bool,
	invalidFeedback: PropTypes.string,
	validFeedback: PropTypes.string,
	isValidMessage: PropTypes.bool,
	isTooltipFeedback: PropTypes.bool,
	onBlur: PropTypes.func,
	onChange: PropTypes.func,
	onFocus: PropTypes.func,
	onInput: PropTypes.func,
	onInvalid: PropTypes.func,
	onSelect: PropTypes.func,
	ariaLabel: PropTypes.string,
};
Checks.defaultProps = {
	id: null,
	className: null,
	name: null,
	type: 'checkbox',
	label: null,
	checked: false,
	disabled: false,
	isInline: false,
	isFormCheckInput: false,
	isTouched: false,
	isValid: false,
	invalidFeedback: null,
	validFeedback: null,
	isValidMessage: true,
	isTooltipFeedback: false,
	onBlur: null,
	onChange: null,
	onFocus: null,
	onInput: null,
	onInvalid: null,
	onSelect: null,
	ariaLabel: null,
};

export default Checks;
